import React, { useState } from 'react'
import { PropertyAnnouncement } from 'types/propertyAnnouncement'
import commonStyles from 'styles/globalStyles'
import VerticalStep, { ISteps } from 'components/VerticalStep'
import PropertyPhotos from './steps/PropertyPhotos'
import PropertyInfo from './steps/PropertyInfo'
import PropertyValues from './steps/PropertyValues'
import PropertyWarranty from './steps/PropertyWarranty'
import Confirmation from './steps/Confirmation'
import { STEPS } from 'constants/announcementSteps'
import { ImageType } from 'react-images-uploading'
import { updateAnnouncement } from 'services/announcements'
import Loader from 'react-loader-spinner'
import COLORS from 'styles/colors'
import { uploadPhoto, deletePhoto } from 'services/image-upload'
import { differenceBy } from 'lodash'
import { createUseStyles } from 'react-jss'

const LOCAL_STORAGE_KEY = 'updated_payload'

type Props = {
  propertyAnnouncement: PropertyAnnouncement
  goToSuccessPage: () => void
}

const EditAnnouncement = (props: Props) => {
  const classes = useStyles(props)
  let localProperty = props.propertyAnnouncement
  const updatedPayload = localStorage.getItem(LOCAL_STORAGE_KEY)
  if (updatedPayload) {
    localProperty = JSON.parse(updatedPayload)
  }

  const [isUpdating, setIsUpdating] = useState(false)
  const [currentStep, setCurrentStep] = useState<string | number>(STEPS.PHOTOS)
  const [propertyTypes, setPropertyTypes] = useState<string[]>([localProperty.info.type])
  const [fullPayload, setFullPayload] = useState(localProperty)
  const propertyImages: ImageType[] = localProperty.images.map((i) => ({
    dataURL: i.secure_url,
  }))
  const [photos, setPhotos] = useState<ImageType[]>(propertyImages)
  const [error, setError] = useState(false)

  const onClickStep = (step: ISteps, isClickable: boolean) => {
    if (isClickable) {
      setCurrentStep(step.id)
    }
  }

  const onClickSave = (payload: Partial<PropertyAnnouncement>, nextStep: string) => {
    const updatedPayload = { ...fullPayload, ...payload }
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(updatedPayload))

    setFullPayload(updatedPayload)
    setCurrentStep(nextStep)
  }

  const onSavePhotos = (photos: ImageType[]) => {
    // console.log('onSavePhotos -> photos', photos)
    setPhotos(photos)
    setCurrentStep(STEPS.INFO)
  }

  const handleConfirm = async () => {
    setIsUpdating(true)
    const token = localStorage.getItem('accessToken') || ''
    const announcementId = props.propertyAnnouncement.id
    try {
      const photosToDelete = differenceBy(propertyImages, photos, 'dataURL')
      for (const photoToDelete of photosToDelete) {
        const imageToDelete = props.propertyAnnouncement.images.find((i) => {
          return photoToDelete.dataURL === i.secure_url
        })
        if (imageToDelete) {
          await deletePhoto(token, announcementId, imageToDelete.id)
        }
      }

      await Promise.all(
        photos.map((p, index) => {
          if (!!p.file) {
            return uploadPhoto(token, p.file, announcementId, index)
          }
          return null
        }),
      )

      await updateAnnouncement(token, announcementId, fullPayload)

      localStorage.removeItem(LOCAL_STORAGE_KEY)

      setIsUpdating(false)
      props.goToSuccessPage()
    } catch (e) {
      setError(true)
      setIsUpdating(false)
      console.log('Couldnt update announcement', { e })
    }
  }

  return (
    <div className={classes.container}>
      <h2 style={commonStyles.general.title}>Confira seu anúncio</h2>
      <p style={commonStyles.general.subtitle}>
        Esses são os dados do seu anúncio. Confira e atualize.
      </p>
      <VerticalStep
        selectedStep={currentStep}
        loading={false}
        onClick={onClickStep}
        steps={[
          {
            title: 'Fotos de seu imóvel',
            content: () => <PropertyPhotos propertyPhotos={photos} onClickSave={onSavePhotos} />,
            id: STEPS.PHOTOS,
          },
          {
            title: 'Dados do seu imóvel',
            content: () => (
              <PropertyInfo
                property={fullPayload}
                propertyTypes={propertyTypes}
                setPropertyType={(type: string) => setPropertyTypes([type])}
                onClickSave={onClickSave}
                onPressBack={() => setCurrentStep(STEPS.PHOTOS)}
              />
            ),
            id: STEPS.INFO,
          },
          {
            title: 'Valores e condições',
            content: () => (
              <PropertyValues
                property={fullPayload}
                onClickSave={onClickSave}
                onPressBack={() => setCurrentStep(STEPS.INFO)}
              />
            ),
            id: STEPS.VALUES,
          },
          {
            title: 'Garantia e Pagamento',
            content: () => (
              <PropertyWarranty
                property={fullPayload}
                onClickSave={onClickSave}
                onPressBack={() => setCurrentStep(STEPS.VALUES)}
              />
            ),
            id: STEPS.WARRANTY,
          },
          {
            title: 'Confirmar e Publicar',
            content: () => (
              <Confirmation
                property={fullPayload}
                onClickSave={() => handleConfirm()}
                onPressBack={() => setCurrentStep(STEPS.VALUES)}
              />
            ),
            id: STEPS.CONFIRMATION,
          },
        ]}
      />
      {!!error && (
        <p className={classes.errorText}>
          Ocorreu um erro ao atualizar o anúncio. Por favor verifique os dados ou tente novamente
          mais tarde.
        </p>
      )}
      {isUpdating && (
        <Loader
          type='Oval'
          color={COLORS.brand.primaryDark}
          height={60}
          width={60}
          style={{ margin: '50px auto', display: 'flex', justifyContent: 'center' }}
        />
      )}
    </div>
  )
}

const useStyles = createUseStyles({
  container: {
    padding: 20,
  },
  errorText: {
    color: COLORS.uiSpecific.danger,
    textAlign: 'center',
  },
})

export default EditAnnouncement
